import React, { useState } from 'react'
import { connect } from 'react-redux'
import EmailVerificationEmailPasswordForm from '../../../components/modals/emailVerification/EmailVerificationEmailPasswordForm'
import EmailVerificationCodeEntry from '../../../components/modals/emailVerification/EmailVerificationCodeEntry'
import ReauthenticationModal from '../../../components/reauthenticationModal/ReauthenticationModal'
import {
  createEmailVerification, resendEmailVerification, verifyEmailVerification,
} from '../../../actions/emailVerification'
import { showNotification } from '../../../actions/notifications'
import getErrorMessageForRequest from '../../../utils/getErrorMessageForRequest'

// Simple, standalone "add a backup email" flow for the account page — just
// email (+ reauth if the session needs it) -> code -> done. This is
// intentionally separate from the bigger email-verification prompt modal.
function AddBackupEmailContainer(props) {
  const {
    user, signinMethods, hideModal,
    createEmailVerification, resendEmailVerification, verifyEmailVerification,
    showNotification,
  } = props

  const [step, setStep] = useState('form') // 'form' | 'code'
  const [verification, setVerification] = useState(null) // { id, email, resendAvailableAt }
  const [loading, setLoading] = useState(false)
  const [formError, setFormError] = useState('')
  // the email being submitted when a 403 reauth_required interrupted it, so
  // the reauth modal's result can resubmit it
  const [pendingEmail, setPendingEmail] = useState(null)
  const [showReauthModal, setShowReauthModal] = useState(false)

  const submitBackupEmail = async (email, authentication) => {
    setLoading(true)
    setFormError('')
    try {
      const response = await createEmailVerification(user.id, { purpose: 'upsertBackup', email, ...authentication })
      setLoading(false)
      setPendingEmail(null)
      setVerification({ id: response.id, email, resendAvailableAt: response.resendAvailableAt })
      setStep('code')
    } catch (err) {
      setLoading(false)
      if (err.response && err.response.status === 403) {
        setPendingEmail(email)
        setShowReauthModal(true)
        return
      }
      setFormError(getErrorMessageForRequest({ requestName: 'createEmailVerification', response: err.response, data: { email } }))
    }
  }

  const handleSubmit = ({ email }) => submitBackupEmail(email)

  const handleReauthenticate = (authentication) => {
    if (!pendingEmail) { return }
    submitBackupEmail(pendingEmail, authentication)
  }

  const handleVerifyCode = async (code) => {
    try {
      await verifyEmailVerification(user.id, verification.id, code, 'upsertBackup', verification.email)
      hideModal()
      showNotification('', 'Backup email added', 'default')
    } catch (err) {
      throw new Error(getErrorMessageForRequest({ requestName: 'verifyEmailVerificationCode', response: err.response }))
    }
  }

  const handleResend = async () => {
    try {
      return await resendEmailVerification(user.id, verification.id)
    } catch (err) {
      throw new Error(getErrorMessageForRequest({ requestName: 'resendEmailVerification', response: err.response }))
    }
  }

  if (step === 'code') {
    return (
      <EmailVerificationCodeEntry
        email={verification.email}
        title='Verify your backup email'
        description='Enter the code we sent to your email'
        resendAvailableAt={verification.resendAvailableAt}
        showIllustration={false}
        onSubmitCode={handleVerifyCode}
        onResend={handleResend}
      />
    )
  }

  return (
    <React.Fragment>
      <EmailVerificationEmailPasswordForm
        title='Add a backup email'
        description='A backup email means you can always get back into your account, even if you lose access to your primary email.'
        showIllustration={false}
        emailLabel='Backup email address'
        submitLabel='Send confirmation code'
        currentEmail={user.email}
        duplicateEmailError='Please enter a different email than your account email.'
        submitLoading={loading}
        errorMessage={formError}
        onSubmit={handleSubmit}
      />
      {showReauthModal && (
        <ReauthenticationModal
          title='Add a Backup Email'
          signinMethodsToDisplay={signinMethods}
          onAuthenticate={handleReauthenticate}
          hideModal={() => setShowReauthModal(false)}
        />
      )}
    </React.Fragment>
  )
}

export default connect(
  (state) => ({ user: state.user, signinMethods: state.signinMethods }),
  {
    createEmailVerification, resendEmailVerification, verifyEmailVerification, showNotification,
  },
)(AddBackupEmailContainer)
